import React, {useMemo, useState} from 'react';
import {Box, ThemeProvider, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography} from '@mui/material';
import NavBar from '../../pieces/navbar/navbar';
import myTheme from '../../styles/myStyles';
import {globalUseClasses} from '../../styles/globalClasses';
import {useGridViewState} from '../../hooks/useGridViewState';
import usePagination from '../../hooks/usePagination';
import MyPaginatedGrid from '../../pieces/grids/paginatedGridWrapper';
import ColumnDefinition from '../../pieces/grids/columnGenerator';
import DisplayListsPageWrapper from '../wrappers/displayListsPageWrapper';
import ListPageHeader from '../../pieces/listPageHeader';
import {ListViewHeader} from '../jobs/components/listViewHeader';
import base_url from '../../baseUrls';
import Utils from '../../utils/utils';

const safeJson = (value) => {
  if (value == null) return null;
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return String(value);
  }
};

const ZohoLogDetailDialog = ({log, onClose}) => (
  <Dialog open={!!log} onClose={onClose} maxWidth="lg" fullWidth>
    <DialogTitle>
      Log Detail
      {log?.statusText && (
        <Typography variant="body2" sx={{mt: 0.5}}>
          <strong>Status Text:</strong> {log.statusText}
        </Typography>
      )}
    </DialogTitle>
    <DialogContent dividers>
      <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
        {[['Request Body', log?.requestBody], ['Response Body', log?.responseBody]].map(([label, body]) => (
          <Box key={label}>
            <Typography variant="subtitle2" sx={{fontWeight: 700, mb: 1}}>{label}</Typography>
            <Box sx={{border: '1px solid #e0e0e0', borderRadius: 1, p: 1, bgcolor: '#fafafa', overflow: 'auto', maxHeight: 420}}>
              <pre style={{margin: 0, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}>
                {safeJson(body) ?? '—'}
              </pre>
            </Box>
          </Box>
        ))}
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

const ZohoLogsGrid = ({viewController, pagination, onViewRow, refreshTimestamp}) => {
  const fetchParams = useMemo(() => ({
    url: `${base_url.api}users/get-zoho-request-logs`,
    respKey: 'logs',
  }), []);

  const components = useMemo(() => ({
    viewBtn: ({data}) => (
      <Button
        size="small"
        variant="outlined"
        sx={{height: 16, fontSize: 11, py: 0, px: 1, minWidth: 0, lineHeight: 1}}
        onClick={() => onViewRow(data)}
      >
        View
      </Button>
    ),
  }), [onViewRow]);

  const columnDefs = useMemo(() => [
    new ColumnDefinition({field: {api_name: 'id', label: ''}, hide: true}),
    new ColumnDefinition({
      field: {api_name: 'view_btn', label: ''},
      width: 80,
      cellRenderer: 'viewBtn',
      removeDefaultFilter: true,
      pinned: 'left',
      lockPinned: true,
    }),
    new ColumnDefinition({
      field: {api_name: 'createdAt', label: 'Timestamp'},
      filter: 'agDateColumnFilter',
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) {
          return -1;
        }
        const parsedDate = new Date(cellValue);
        if (Number.isNaN(parsedDate.getTime())) {
          return -1;
        }
        const cellDate = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());

        if (cellDate < filterLocalDateAtMidnight) {
          return -1;
        }
        if (cellDate > filterLocalDateAtMidnight) {
          return 1;
        }
        return 0;
      },
      valueGetter: ({data}) => data?.createdAt ? Utils.formatIsoDateString(data.createdAt) : '—',
      width: 200,
    }),
    new ColumnDefinition({
      field: {api_name: 'direction', label: 'Direction'},
      filter: 'agSetColumnFilter',
      filterValues: ['inbound', 'outbound'],
      valueGetter: ({data}) => {
        const value = data?.direction;
        return value == null ? '' : String(value).toLowerCase();
      },
      width: 150,
    }),
    new ColumnDefinition({
      field: {api_name: 'method', label: 'Method'},
      filter: 'agSetColumnFilter',
      filterValues: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS', 'TRACE', 'CONNECT'],
      valueGetter: ({data}) => {
        const value = data?.method;
        return value == null ? '' : String(value).toUpperCase();
      },
      width: 120,
    }),
    new ColumnDefinition({field: {api_name: 'path', label: 'Path'}}),
    new ColumnDefinition({field: {api_name: 'status', label: 'Status'}, width: 100, filter: 'agNumberColumnFilter'}),
    new ColumnDefinition({
      field: {api_name: 'ok', label: 'Result'},
      valueGetter: ({data}) => {
        if (data?.ok === true) return 'OK';
        if (data?.ok === false) return 'Failed';
        return '—';
      },
      width: 100,
    }),
    new ColumnDefinition({
      field: {api_name: 'durationMs', label: 'Duration'},
      valueGetter: ({data}) => data?.durationMs != null ? `${data.durationMs}ms` : '—',
      width: 110,
    }),
  ], []);

  return (
    <MyPaginatedGrid
      columnDefs={columnDefs}
      components={components}
      contextMenu={() => ['copy']}
      rowHeight={35}
      colDefOverrides={{ autoHeight: false }}
      gridOptionsOverrides={{ animateRows: false, rowBuffer: 25 }}
      ls_name="zoho_logs_grid_v2"
      registerGrid={viewController.setGridToParent}
      registerTempGrid={viewController.setTempGridToParent}
      fetchParams={fetchParams}
      onFetchFail={viewController.onFetchFail}
      pagination={pagination}
      refreshTimestamp={refreshTimestamp}
    />
  );
};

const ZohoLogsView = () => {
  const classes = globalUseClasses();
  const viewController = useGridViewState();
  const pagination = usePagination();
  const [selectedLog, setSelectedLog] = useState(null);

  const getHeaderComponent = () => {
    let title = 'Zoho API Logs';
    if (pagination.totalItemsCount != null) title = `${title} (${pagination.totalItemsCount})`;
    return (
      <ListViewHeader
        title={title}
        pagination={pagination}
        viewController={viewController}
      />
    );
  };

  const headerComponent = (
    <ListPageHeader
      viewController={viewController}
      headComponent={getHeaderComponent()}
      totalItems={pagination.totalItemsCount}
      showAddButton={false}
      additionalButtons={[
        {
          label: 'Refresh Grid',
          variant: 'outlined',
          color: 'primary',
          onClick: viewController.refreshGrid,
        },
      ]}
    >
      {pagination.ItemsController}
    </ListPageHeader>
  );

  return viewController.RenderElement(
    <>
      <NavBar/>
      <ThemeProvider theme={myTheme}>
        <Box className={classes.page}>
          <DisplayListsPageWrapper headerComponent={headerComponent}>
            <ZohoLogsGrid
              viewController={viewController}
              pagination={pagination}
              onViewRow={setSelectedLog}
              refreshTimestamp={viewController.refreshTimestamp}
            />
            {pagination.PageController}
          </DisplayListsPageWrapper>
        </Box>
      </ThemeProvider>
      <ZohoLogDetailDialog log={selectedLog} onClose={() => setSelectedLog(null)} />
    </>
  );
};

export default ZohoLogsView;
